import { FC, forwardRef, ReactNode, Ref } from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import cn from 'classnames';

import { Icon } from '../../../components/atoms/icon/Icon';
import { InputValidationStatus } from '../input/Input';

import './select.scss';

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
  hidden?: boolean;
}

export const SelectRoot: FC<RadixSelect.SelectProps> = (props) => <RadixSelect.Root {...props} />;
export const SelectTrigger: FC<RadixSelect.SelectTriggerProps> = (props) => (
  <RadixSelect.Trigger className="lsux-select__trigger" {...props} />
);
export const SelectGroup: FC<RadixSelect.SelectGroupProps> = (props) => <RadixSelect.Group {...props} />;
export const SelectLabel: FC<RadixSelect.SelectLabelProps> = (props) => (
  <RadixSelect.Label className="lsux-select-item--label" {...props} />
);
export const SelectSeparator: FC<RadixSelect.SelectSeparatorProps> = (props) => (
  <RadixSelect.Separator className="lsux-select-item--separator" {...props} />
);

export interface SelectItemProps extends RadixSelect.SelectItemProps {
  classNames?: cn.Argument;
}

export const SelectItem: FC<SelectItemProps> = ({ children, classNames, ...props }) => {
  let textValue = props.textValue || '';
  if (typeof children === 'string' && children.length > 0) {
    textValue = children;
  }

  return (
    <RadixSelect.Item
      {...props}
      className={cn('lsux-select-item', classNames)}
      disabled={props.disabled}
      value={props.value}
      textValue={textValue}
    >
      <RadixSelect.ItemIndicator className="lsux-select-item--indicator">
        <Icon name="interface_check" size="small" />
      </RadixSelect.ItemIndicator>
      <RadixSelect.ItemText>{textValue}</RadixSelect.ItemText>
    </RadixSelect.Item>
  );
};
SelectItem.displayName = 'SelectItem';

export interface SelectProps extends RadixSelect.SelectProps {
  /**
   * Value of the select component.
   */
  value?: string;
  /**
   * Value of the component.
   * @note This will render the options as a list of `SelectItem` components. It overwrites any children passed to the component.
   */
  options?: SelectOption[];
  /**
   * Triggers error | warning | success styles on the component. Important for accessibility.
   * @type {InputValidationStatus}
   * @default undefined
   */
  status?: InputValidationStatus;
  /**
   * Placeholder text to display.
   * @default 'Select an option'
   */
  placeholder?: string;
  /**
   * Items to render in the select component.
   * @description This expects the `SelectItem` component.
   */
  children?: ReactNode;
  /**
   * Stretch the select component to fill the width of the parent container.
   * @default false
   */
  stretch?: boolean;
  /**
   * Custom classes for the component.
   */
  classNames?: cn.Argument;
  /**
   * The ref to the HTML DOM element.
   */
  ref?: Ref<HTMLButtonElement>;
}

const SelectComponent = forwardRef<HTMLButtonElement, SelectProps>(
  (
    { children, options, value, placeholder = 'Select an option', status, stretch = false, classNames = [], ...props },
    ref: SelectProps['ref'],
  ) => {
    const classSelectTrigger = 'lsux-select__trigger';
    const cnSelectTrigger = cn({
      [`${classSelectTrigger}`]: true,
      [`${classSelectTrigger}--${status}`]: status,
      [`${classSelectTrigger}--stretch`]: stretch,
    });

    // Warn if both children and options are passed
    if (children && options) {
      console.warn(
        'Select component: Both children and options are passed to the component. Only options will be rendered.',
      );
    }

    if (options) {
      children = (
        <>
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              hidden={option.hidden}
              textValue={option.label}
            />
          ))}
        </>
      );
    }

    return (
      <RadixSelect.Root {...props} onValueChange={props.onValueChange} value={value} disabled={props.disabled}>
        <RadixSelect.Trigger
          ref={ref}
          aria-label={props.name}
          className={cn(cnSelectTrigger, classNames)}
          placeholder={placeholder}
        >
          <div className="lsux-select__trigger__row">
            <RadixSelect.Value className="lsux-select__trigger__value" placeholder={placeholder} />
            <RadixSelect.Icon className="lsux-select__trigger__icon">
              <Icon name="arrow_caret_down_md" />
            </RadixSelect.Icon>
          </div>
        </RadixSelect.Trigger>
        <RadixSelect.Portal>
          <RadixSelect.Content className="lsux-select__content">
            <RadixSelect.ScrollUpButton className="lsux-select__content__button">
              <Icon name="arrow_caret_up_md" />
            </RadixSelect.ScrollUpButton>
            <RadixSelect.Viewport className="lsux-select__viewport">{children}</RadixSelect.Viewport>
            <RadixSelect.ScrollDownButton className="lsux-select__content__button">
              <Icon name="arrow_caret_down_md" />
            </RadixSelect.ScrollDownButton>
            <RadixSelect.Arrow />
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    );
  },
) as FC<SelectProps>;

SelectComponent.displayName = 'Select';

type SelectRootComponent = FC<RadixSelect.SelectProps>;
type SelectTriggerComponent = FC<RadixSelect.SelectTriggerProps>;
type SelectItemComponent = FC<SelectItemProps>;
type SelectGroupComponent = FC<RadixSelect.SelectGroupProps>;
type SelectLabelComponent = FC<RadixSelect.SelectLabelProps>;
type SelectSeparatorComponent = FC<RadixSelect.SelectSeparatorProps>;

type SelectComp = FC<SelectProps> & {
  Root: SelectRootComponent;
  Trigger: SelectTriggerComponent;
  Item: SelectItemComponent;
  Group: SelectGroupComponent;
  Label: SelectLabelComponent;
  Separator: SelectSeparatorComponent;
};

export const Select = SelectComponent as SelectComp;

Select.Root = SelectRoot;
Select.Trigger = SelectTrigger;
Select.Item = SelectItem;
Select.Group = SelectGroup;
Select.Label = SelectLabel;
Select.Separator = SelectSeparator;
